import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col } from 'reactstrap';
import moment from 'moment';

interface UserData {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  groupId?: string | null;
}

interface ReferenceObject {
  name?: string | null;
}

interface NewHireData {
  id: string;
  userId?: string | null;
  user?: UserData | null;
  schoolId?: string | null;
  school?: ReferenceObject | null;
  franchiseeId?: string | null;
  franchisee?: ReferenceObject | null;
  titleId?: string | null;
  title?: ReferenceObject | null;
  departmentId?: string | null;
  department?: ReferenceObject | null;
  supervisorId?: string | null;
  supervisor?: ReferenceObject | null;
  ptoPolicyId?: string | null;
  ptoPolicy?: ReferenceObject | null;
  offerLetter?: string | null;
  classroom?: string;
  education?: string;
  experienceEce?: string;
  goodFitReason?: string;
  startDate?: string | null;
  hoursPerWeek?: number | null;
  workday?: string | null;
  paymentFrequency?: string | null;
  payRate?: string | null;
  status?: string | null;
}

interface NewHireReviewModalProps {
  isOpen: boolean;
  toggle: () => void;
  data: NewHireData | null;
  groupsList?: { id: string; name: string }[];
}

const NewHireReviewModal: React.FC<NewHireReviewModalProps> = ({ isOpen, toggle, data }) => {
  const formatCurrency = (value: string | undefined | null): string => {
    if (value === undefined || value === null || value === '') return '-';
    const cleanedValue = value.replace(/[^0-9.]/g, '');
    const numberValue = parseFloat(cleanedValue);
    if (isNaN(numberValue)) return value;
    return `$${numberValue.toFixed(2)}`;
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return '-';
    const date = moment(dateString, 'YYYY-MM-DD');
    return date.isValid() ? date.format('MM/DD/YYYY') : dateString;
  };

  if (!data) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} style={{ paddingBottom: 0 }}>
        Review New Hire Information
      </ModalHeader>
      <ModalBody style={{ paddingTop: 0 }}>
        <h5>Basic Information</h5>
        <hr className="mt-1 mb-3" />
        <Row className="mb-2">
          <Col md="4">
            <strong>First Name:</strong>
          </Col>
          <Col md="8">{data.user?.firstName || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Last Name:</strong>
          </Col>
          <Col md="8">{data.user?.lastName || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Email Address:</strong>
          </Col>
          <Col md="8">{data.user?.email || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Phone Number:</strong>
          </Col>
          <Col md="8">{data.user?.phone || '-'}</Col>
        </Row>

        <h5 className="mt-4">Job Details</h5>
        <hr className="mt-1 mb-3" />
        <Row className="mb-2">
          <Col md="4">
            <strong>Job Title:</strong>
          </Col>
          <Col md="8">{data.title?.name || data.titleId || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Department:</strong>
          </Col>
          <Col md="8">{data.department?.name || data.departmentId || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Supervisor:</strong>
          </Col>
          <Col md="8">{data.supervisor?.name || data.supervisorId || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Hire Date:</strong>
          </Col>
          <Col md="8">{formatDate(data.startDate)}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Work Location (School):</strong>
          </Col>
          <Col md="8">{data.school?.name || data.schoolId || '-'}</Col>
        </Row>

        <h5 className="mt-4">Compensation & Classification</h5>
        <hr className="mt-1 mb-3" />
        <Row className="mb-2">
          <Col md="4">
            <strong>Classification:</strong>
          </Col>
          <Col md="8">{data.workday || '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Hours Per Week:</strong>
          </Col>
          <Col md="8">{data.hoursPerWeek ?? '-'}</Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Payment Frequency:</strong>
          </Col>
          <Col md="8">
            {data.paymentFrequency
              ? data.paymentFrequency.charAt(0).toUpperCase() + data.paymentFrequency.slice(1)
              : '-'}
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>Pay Rate:</strong>
          </Col>
          <Col md="8">
            {formatCurrency(data.payRate)} {data.paymentFrequency === 'hourly' ? '/ hour' : '/ year'}
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md="4">
            <strong>PTO Policy:</strong>
          </Col>
          <Col md="8">{data.ptoPolicy?.name || data.ptoPolicyId || '-'}</Col>
        </Row>

        <h5 className="mt-4">Additional Information</h5>
        <hr className="mt-1 mb-3" />
        <Row className="mb-2">
          <Col md="4">
            <strong>Offer Letter:</strong>
          </Col>
          <Col md="8">{data.offerLetter ? `Template: ${data.offerLetter}` : 'Not Included'}</Col>
        </Row>

        <Row className="mb-2">
          <Col md="4">
            <strong>Which Classroom?</strong>
          </Col>
          <Col md="8">{data?.classroom || '-'}</Col>
        </Row>

        <Row className="mb-2">
          <Col md="4">
            <strong>Education:</strong>
          </Col>
          <Col md="8">{data?.education || '-'}</Col>
        </Row>

        <Row className="mb-2">
          <Col md="4">
            <strong>Experience in ECE:</strong>
          </Col>
          <Col md="8">{data?.experienceEce || '-'}</Col>
        </Row>

        <Row className="mb-2">
          <Col md="4">
            <strong>Reason for Good Fit:</strong>
          </Col>
          <Col md="8" style={{ whiteSpace: 'pre-wrap' }}>
            {data?.goodFitReason || '-'}
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md="4">
            <strong>Current Status:</strong>
          </Col>
          <Col md="8">{data.status ? String(data.status)?.replace('_', ' ')?.toUpperCase() : '-'}</Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NewHireReviewModal;
